/**
 * RDKit WASM lazy-loader.
 *
 * A single shared promise so the WASM module is only imported once per
 * session. Multiple medchem modules (`structuralAlertScanner`,
 * `compoundTransform`, future additions) call `loadRDKit()` and await
 * the same promise.
 *
 * On import or initialization failure the cached promise is cleared so
 * a later call can retry — useful when the WASM is temporarily
 * unavailable (e.g., a Node test environment with no `fetch`).
 *
 * @module gibbs/knowledge/rdkitLoader
 */

let rdkitPromise = null;

/**
 * Lazy-load the RDKit WASM module. Returns a promise resolving to the
 * `RDKitExport` class (which provides `getMol`, `getQMol`, `isInitialized`,
 * etc.). Throws if the module cannot be loaded.
 *
 * @returns {Promise<typeof import('../../plugin/RDKitExport').RDKitExport>}
 */
export function loadRDKit() {
    if (rdkitPromise) return rdkitPromise;
    rdkitPromise = import('../../plugin/RDKitExport')
        .then((mod) => mod.RDKitExport)
        .catch((err) => {
            rdkitPromise = null; // allow retry
            throw err;
        });
    return rdkitPromise;
}

/**
 * Reset the cached loader. Test-only — production callers should rely on
 * the natural cache.
 */
export function resetRDKitLoaderForTest() {
    rdkitPromise = null;
}
